/**
 * MONTHLY PAYOUT ON A HIRE ORDER, AT FULL USE — asked by the CMD, 31-10-2026.
 *
 * "What will this order cost me each month if every machine (or piece) is on
 * site every day and nothing goes wrong?" Read straight off the order: no log
 * sheet, no challan, nothing certified. It is a forecast, and the card that
 * shows it says so.
 *
 * HOW A MONTH IS BUILT
 *   Hire is billed by the day. Each line runs from the order's start date for
 *   the days it buys (quantity / units), so a calendar month pays its own days:
 *   30 in November, 31 in December, 28 in February. The last month is whatever
 *   days are left. Each line's months add back to the line's amount to the
 *   paisa: the last month takes the rounding.
 *
 * WHAT COMES OFF EACH BILL, in the order's own terms
 *   GST       per line (gst.ts): paid to the contractor when he charges it;
 *             under reverse charge OVAL pays it to the Government instead
 *   TDS       the order's TDS % on the value before GST
 *   Retention the order's retention % on the value, when it has one
 *   Advance   the order's recovery % of each bill's value, until the advance
 *             (contract value x advance %) is fully back
 */

import { gstSplit, type GstHeader, type GstPayer, gstPayer } from './gst'

const n = (v: unknown) => Number(v ?? 0) || 0
const r2 = (v: number) => Math.round(v * 100) / 100

export interface PayoutLine {
  id: string
  quantity: number
  rate: number
  amount: number
  hire_units: number | null
  gst_rate_percent: number | null
  verdict?: string | null
}

export interface PayoutOrder extends GstHeader {
  start_date: string | null
  contract_value: number
  tds_pct: number | null
  retention_pct: number | null
  advance_pct: number | null
  advance_recovery_pct: number | null
}

export interface PayoutMonth {
  /** YYYY-MM-01 */
  month: string
  /** Days in the month the longest line is on hire. */
  days: number
  value: number
  /** GST the contractor charges (paid to him). */
  gstCharged: number
  /** GST OVAL pays the Government under reverse charge. */
  gstRcm: number
  bill: number
  tds: number
  retention: number
  advance: number
  /** What reaches the contractor's bank account. */
  net: number
}

export interface Payout {
  months: PayoutMonth[]
  advancePaid: number
  totals: Omit<PayoutMonth, 'month' | 'days'>
}

function parse(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}
const DAY = 86_400_000
const monthKey = (d: Date) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`

/** Days of [start, start + days - 1] falling in each calendar month. */
export function daysByMonth(startIso: string, days: number): Map<string, number> {
  const out = new Map<string, number>()
  const start = parse(startIso)
  for (let i = 0; i < Math.max(0, Math.round(days)); i++) {
    const k = monthKey(new Date(start.getTime() + i * DAY))
    out.set(k, (out.get(k) ?? 0) + 1)
  }
  return out
}

export function hirePayout(order: PayoutOrder, lines: PayoutLine[]): Payout | null {
  if (!order.start_date || lines.length === 0) return null

  const byMonth = new Map<string, PayoutMonth>()
  const blank = (month: string): PayoutMonth => ({
    month, days: 0, value: 0, gstCharged: 0, gstRcm: 0, bill: 0,
    tds: 0, retention: 0, advance: 0, net: 0,
  })

  for (const l of lines) {
    const units = n(l.hire_units) || 1
    const lineDays = n(l.quantity) / units
    const split = daysByMonth(order.start_date, lineDays)
    const keys = [...split.keys()]
    const payer: GstPayer = gstPayer(order, l.verdict)
    let given = 0
    keys.forEach((k, i) => {
      const d = split.get(k) ?? 0
      const value = i === keys.length - 1
        ? r2(n(l.amount) - given)
        : r2(units * n(l.rate) * d)
      given = r2(given + value)
      const g = gstSplit(value, n(l.gst_rate_percent), payer, order.gst_treatment)
      const m = byMonth.get(k) ?? blank(k)
      m.days = Math.max(m.days, d)
      m.value = r2(m.value + value)
      if (payer === 'CHARGED') m.gstCharged = r2(m.gstCharged + g.gst)
      if (payer === 'RCM') m.gstRcm = r2(m.gstRcm + g.gst)
      byMonth.set(k, m)
    })
  }

  const advancePaid = r2(n(order.contract_value) * n(order.advance_pct) / 100)
  let toRecover = advancePaid
  const months = [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month))
  for (const m of months) {
    m.bill = r2(m.value + m.gstCharged)
    m.tds = r2(m.value * n(order.tds_pct) / 100)
    m.retention = r2(m.value * n(order.retention_pct) / 100)
    m.advance = toRecover > 0
      ? Math.min(toRecover, r2(m.value * n(order.advance_recovery_pct) / 100))
      : 0
    toRecover = r2(toRecover - m.advance)
    m.net = r2(m.bill - m.tds - m.retention - m.advance)
  }

  const sum = (f: (m: PayoutMonth) => number) => r2(months.reduce((s, m) => s + f(m), 0))
  return {
    months,
    advancePaid,
    totals: {
      value: sum((m) => m.value), gstCharged: sum((m) => m.gstCharged),
      gstRcm: sum((m) => m.gstRcm), bill: sum((m) => m.bill), tds: sum((m) => m.tds),
      retention: sum((m) => m.retention), advance: sum((m) => m.advance),
      net: sum((m) => m.net),
    },
  }
}
